'use client';

import { SanityDocument } from 'next-sanity';
import { useOptimistic } from 'next-sanity/hooks';

import BlockRenderer from './BlockRenderer';

type PageBuilderSection = {
  _key: string;
  _type: string;
};

type AnyPageData = {
  _id: string;
  _type: string;
  pageBuilder?: PageBuilderSection[] | null;
  [key: string]: unknown;
} | null;

type PageBuilderPageProps = {
  page: AnyPageData;
};

type PageData = {
  _id: string;
  _type: string;
  pageBuilder?: PageBuilderSection[] | null;
};

type MinimalPage = {
  _id: string;
  _type: string;
};

/**
 * The PageBuilder component is used to render the blocks from the `pageBuilder` field in the Page type in your Sanity Studio.
 */

function renderSections(pageBuilderSections: PageBuilderSection[], page: AnyPageData) {
  if (!page) {
    return null;
  }
  return (
    <div>
      {pageBuilderSections
        .filter((b: any) => b && typeof b._type === 'string')
        .map((block: any, index: number) => (
          <BlockRenderer
            key={block?._key ?? `${block?._type ?? 'block'}-${index}`}
            index={index}
            block={block}
            pageId={page._id}
            pageType={page._type}
          />
        ))}
    </div>
  );
}

function renderEmptyState(page: MinimalPage) {
  if (!page) {
    return null;
  }
  return (
    <div className="container">
      <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">This page has no content!</h1>
      <p className="mt-2 text-base text-secondary-50">Open the page in Sanity Studio to add content.</p>
    </div>
  );
}

export default function PageBuilder({ page }: PageBuilderPageProps) {
  const pageBuilderSections = useOptimistic<PageBuilderSection[], SanityDocument<PageData>>(
    (page?.pageBuilder as PageBuilderSection[]) || [],
    (currentSections, action) => {
      // The action contains updated document data from Sanity
      // when someone makes an edit in the Studio

      // If the edit was to a different document, ignore it
      if (action.id !== page?._id) {
        return currentSections;
      }

      // If there are sections in the updated document, use them
      if (action.document.pageBuilder) {
        // Reconcile References. https://www.sanity.io/docs/enabling-drag-and-drop#ffe728eea8c1
        return action.document.pageBuilder.map((section: PageBuilderSection) => {
          return currentSections?.find((s) => s._key === section?._key) || section;
        });
      }

      // Otherwise keep the current sections
      return currentSections;
    },
  );

  if (!page) {
    return null;
  }

  return pageBuilderSections && pageBuilderSections.length > 0
    ? renderSections(pageBuilderSections, page as any)
    : renderEmptyState({ _id: page._id, _type: page._type });
}
